import { useState } from "react";
import Layout from "@/components/feature/Layout";
import SectionHeader from "@/components/base/SectionHeader";
import { Link } from "react-router-dom";
import FinalCta from "@/pages/home/components/FinalCta";

type Category = "All" | "Client Work" | "Concept Project" | "Digital Experiment";

const categories: Category[] = [
  "All",
  "Client Work",
  "Concept Project",
  "Digital Experiment",
];

type Project = {
  category: Category;
  title: string;
  desc: string;
  features: string[];
  demoUrl?: string;
  image: string;
};

const projects: Project[] = [
  {
    category: "Concept Project" as const,
    title: "NÉVORA SKIN",
    desc: "NÉVORA is a modern skincare brand focused on simple, effective daily skincare. Note: This is just for demo purpose, no real backend has been connected",
    features: ["React", "Responsive", "E-Commerce"],
    demoUrl: "https://jazzy-nougat-2add2d.netlify.app/",
    image:"/images/concept02.png",
  },
  {
    category: "Digital Experiment" as const,
    title: "Flow Automation",
    desc: "A prototype exploring how manual workflows can be turned into intelligent, automated processes.",
    features: ["Automation", "AI", "APIs"],
    image:
      "https://readdy.ai/api/search-image?query=An%20abstract%20digital%20automation%20concept%20visualization%2C%20connected%20nodes%20and%20flowing%20data%20streams%20on%20a%20clean%20dark%20background%2C%20glowing%20red%20and%20white%20connection%20lines%2C%20futuristic%20workflow%20technology%20illustration%2C%20minimal%20premium%20technology%20art&width=800&height=600&seq=emchon-work-03&orientation=landscape",
  },
  {
    category: "Concept Project" as const,
    title: "AI Tutor",
    desc: "A concept for an AI platform to help students prepare for WAEC, NECO and JAMB. Note: This is just for demo purpose, no real backend or AI model has been connected.",
    features: ["AI Chatbot", "Practice Questions", "Mock Examinations", "Admin Dashboard"],
    demoUrl: "https://regal-pithivier-b42f1b.netlify.app/",
    image:"/images/concept01.png",
  },
  {
    category: "Client Work",
    title: "Connect-Verse",
    desc: "A paid membership dating platform where members can meet and interact with single Men/Women from around the country.",
    features: ["React", "Paystack", "Node.js"],
    image:"/images/mockup2.png",
  },
  {
    category: "Client Work",
    title: "ACS - RSU Chapter",
    desc: "A clean, modern and mobile responsive website for the Rivers State University Chapter of the American Chemical Society(ACS).",
    features: ["Next.js", "Tailwind CSS", "SEO"],
    image:"/images/mockup1.png",
  },
  {
    category: "Client Work",
   title: "Atlas Logistics",
    desc: "A shipment-tracking portal for a regional logistics company, giving customers real-time visibility into every order.",
    features: ["React", "PostgreSQL", "APIs"],
    image:
      "https://readdy.ai/api/search-image?query=A%20shipment%20tracking%20logistics%20dashboard%20interface%20mockup%20on%20a%20clean%20light%20background%2C%20modern%20delivery%20management%20web%20app%20with%20maps%20and%20data%20panels%2C%20red%20accent%20highlights%2C%20professional%20SaaS%20UI%20presentation%2C%20crisp%20and%20detailed&width=800&height=600&seq=emchon-work-07&orientation=landscape",
  },
];

export default function Work() {
  const [active, setActive] = useState<Category>("All");

  const filtered =
    active === "All" ? projects : projects.filter((p) => p.category === active);

  return (
    <Layout>
      <div className="pt-16 md:pt-[72px]">
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
          <div className="mx-auto max-w-[1280px]">
            <SectionHeader
              eyebrow="Selected Work"
              title="A look at the digital experiences and solutions we've designed and built."
              subtitle="We present our work honestly — client projects are shown as they're delivered, alongside clearly labeled concept work and experiments."
            />
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20">
          <div className="mx-auto max-w-[1280px]">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActive(c)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    active === c
                      ? "bg-primary-500 text-foreground-950"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {filtered.map((p) => (
                  <div
                    key={p.title}
                    className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
                  >
                    <div className="relative h-60 w-full overflow-hidden bg-background-100">
                      <img
                        src={p.image}
                        alt={p.title}
                        title={`${p.title} — ${p.category} by EMCHON Technologies`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-[11px] font-semibold text-foreground-950 tracking-wide">
                        {p.category === "Concept Project"
                          ? "CONCEPT PROJECT — BUILT BY EMCHON"
                          : p.category === "Digital Experiment"
                          ? "DIGITAL EXPERIMENT"
                          : "CLIENT WORK"}
                      </span>
                    </div>

                    <div className="p-7">
                      <h3 className="font-heading text-xl font-bold text-foreground-950">
                        {p.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                        {p.desc}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {p.features.map((t) => (
                          <span
                            key={t}
                            className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className="mt-5 flex items-center gap-4">
                        {p.demoUrl ? (
                          <a
                            href={p.demoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-foreground-950 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            View Demo <i className="ri-external-link-line"></i>
                          </a>
                        ) : null}
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap"
                        >
                          Start a Similar Project <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
              ))}
            </div>
          </div>
        </section>

        <FinalCta />
      </div>
    </Layout>
  );
}